/**
 * W-050 — Plans list. Stories: US-A11.
 * Price, duration, scope, active state, drag-to-reorder, recommended flag.
 */
import { DeleteButton, EditButton, List, useTable } from '@refinedev/antd'
import { useCan, useUpdate } from '@refinedev/core'
import { Space, Switch, Tag, Typography } from 'antd'
import { useState } from 'react'
import { ModelInsights, OrderableTable, StatusTag } from '@/components'
import { ConfirmDialog } from '@/components/dialogs'
import { recordAudit } from '@/lib/audit'
import { persistOrder } from '@/lib/order'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import { formatPrice } from '@horizon/i18n'
import type { Plan } from '@/types'

const SCOPE_LABEL_KEYS: Record<Plan['scope'], string> = {
  all: 'admin.plans.scopeAll',
  subjects: 'admin.plans.scopeSubjects',
  program: 'admin.plans.scopeProgram',
  stream: 'admin.plans.scopeStream',
}

export const PlanListPage = () => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const { tableProps, tableQueryResult } = useTable<Plan>({
    resource: 'plans',
    sorters: { initial: [{ field: 'sort_order', order: 'asc' }] },
    pagination: { pageSize: 50 },
  })
  const { data: canEdit } = useCan({ resource: 'plans', action: 'edit' })
  const { mutate: update } = useUpdate()
  const [planToDeactivate, setPlanToDeactivate] = useState<Plan | null>(null)

  const rows = (tableProps.dataSource ?? []) as Plan[]

  return (
    <List
      title={translate('admin.pages.plans')}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.pages.plansSubtitle'),
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="plans" />
        <Typography.Text type="secondary">{translate('admin.plans.note')}</Typography.Text>

        <OrderableTable<Plan>
          {...tableProps}
          dataSource={rows}
          scroll={{ x: 940 }}
          reorderDisabled={!canEdit?.can}
          onReorder={ordered => {
            void persistOrder('plans', ordered)
              .then(() => tableQueryResult.refetch())
              .catch((error: unknown) => console.error('[plans] reorder failed', error))
          }}
          columns={[
            { title: translate('admin.plans.columns.nameFrench'), dataIndex: 'name_fr' },
            {
              title: translate('admin.plans.columns.nameArabic'),
              dataIndex: 'name_ar',
              render: (value: string) => <span dir="rtl">{value}</span>,
            },
            {
              title: translate('admin.plans.columns.price'),
              dataIndex: 'price_mru',
              render: (value: number) => formatPrice(value, locale),
            },
            {
              title: translate('admin.plans.columns.duration'),
              dataIndex: 'duration_days',
              render: (value: number) => `${value} ${translate('admin.plans.days')}`,
            },
            {
              title: translate('admin.plans.columns.scope'),
              dataIndex: 'scope',
              render: (value: Plan['scope']) => <Tag>{translate(SCOPE_LABEL_KEYS[value])}</Tag>,
            },
            {
              title: translate('admin.plans.columns.recommended'),
              dataIndex: 'is_recommended',
              render: (value: boolean) =>
                value ? <Tag color="success">{translate('admin.plans.yes')}</Tag> : '—',
            },
            {
              title: translate('admin.plans.columns.active'),
              dataIndex: 'is_active',
              render: (value: boolean, record: Plan) => (
                <Space>
                  <StatusTag kind="published" value={value} />
                  <Switch
                    size="small"
                    checked={value}
                    disabled={!canEdit?.can}
                    onChange={next => {
                      if (!next) {
                        setPlanToDeactivate(record)
                        return
                      }
                      update({ resource: 'plans', id: record.id, values: { is_active: true } })
                    }}
                  />
                </Space>
              ),
            },
            {
              title: translate('admin.plans.columns.actions'),
              dataIndex: 'id',
              render: (_: unknown, record: Plan) => (
                <Space>
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton
                    hideText
                    size="small"
                    recordItemId={record.id}
                    confirmTitle={translate('admin.plans.deleteConfirmation')}
                    confirmOkText={translate('buttons.delete')}
                    confirmCancelText={translate('buttons.cancel')}
                  />
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <ConfirmDialog
        open={Boolean(planToDeactivate)}
        title={translate('admin.plans.deactivateTitle')}
        description={
          planToDeactivate
            ? translate('admin.plans.deactivateDescription', {
                name:
                  locale === 'ar'
                    ? planToDeactivate.name_ar || planToDeactivate.name_fr
                    : planToDeactivate.name_fr || planToDeactivate.name_ar,
              })
            : ''
        }
        confirmLabel={translate('admin.plans.deactivate')}
        danger
        onCancel={() => setPlanToDeactivate(null)}
        onConfirm={() => {
          if (!planToDeactivate) return
          update({
            resource: 'plans',
            id: planToDeactivate.id,
            values: { is_active: false },
          })
          void recordAudit({
            action: 'plan.deactivate',
            target: 'plans',
            targetId: planToDeactivate.id,
            before: { is_active: true },
            after: { is_active: false },
          })
          setPlanToDeactivate(null)
        }}
      />
    </List>
  )
}
